import React from "react";
import { batch } from "react-redux";
import { useHistory } from "react-router";
import { AnyAction } from "redux";
import { useProfileFetchFunc } from ".";
import { showErrorToast } from "../../components/toast.component";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { AxiosAuth } from "../../core/axios";
import { StorageAuth } from "../../core/localStorage";
import {
  ActionApp,
  ActionAuth,
  ActionUser,
  useAppDispatch,
  useAppSelector,
} from "../../core/redux";
import { ApiResponseContainer } from "../../models/api";
import { ApiSchemaLogin } from "../../models/apiSchema/login";

type LoginRequestBody = {
  userName: string;
  password: string;
  rememberMe: boolean;
};

type AuthFunctions = {
  isBusy: boolean;
  logIn: (
    body: LoginRequestBody,
    redirectOnLogin?: boolean
  ) => Promise<boolean>;
  logOut: () => void;
  redirectToDefaultRoute: () => void;
};

export function useAuthFunc(): AuthFunctions {
  const isRequestOngoing = React.useRef<boolean>(false);
  const [isBusy, setIsBusy] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();

  const store = useAppSelector((state) => ({
    isLoggedIn: state.auth.isLoggedIn,
    role: state.auth.userRole,
  }));

  const { push: historyPush } = useHistory();
  const profile = useProfileFetchFunc();

  const logIn = React.useCallback(
    async (body: LoginRequestBody, redirectOnLogin: boolean = true) => {
      if (isRequestOngoing.current) return false;
      isRequestOngoing.current = true;
      let returnValue = false;
      setIsBusy(true);
      try {
        const response = await AxiosAuth.post<
          ApiResponseContainer<ApiSchemaLogin>
        >(AppRouteApi.Auth.Login(), body);
        if (response.data?.isSuccess) {
          const authData = response.data.data;
          StorageAuth.AccessToken = authData?.token;
          StorageAuth.Email = authData?.emailAddress;
          StorageAuth.UserName = authData?.userName;

          const userRole = authData?.roles?.[0];
          StorageAuth.Role = userRole;

          const reduxActions: AnyAction[] = [];
          const actions: (() => unknown | void)[] = [];

          reduxActions.push(ActionAuth.SetIsLoggedIn(true));

          if (userRole) {
            reduxActions.push(ActionAuth.SetUserRole(userRole));
            if (redirectOnLogin)
              actions.push(() =>
                historyPush(AppRouteUi.GetRouteByRole(userRole))
              );

            if (authData?.menuBuilder) {
              reduxActions.push(ActionUser.SetMenuItems(authData.menuBuilder));
            }
          }
          if (authData?.userName)
            reduxActions.push(ActionAuth.SetUserName(authData?.userName));

          await profile.fetchProfile();

          batch(() => reduxActions.forEach(dispatch));
          actions.forEach((func) => func());
          returnValue = true;
        } else {
          showErrorToast(response.data);
        }
      } catch (e: any) {
        showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
      }
      isRequestOngoing.current = false;
      setIsBusy(false);
      return returnValue;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, historyPush]
  );

  // const logOut = React.useCallback(async () => {
  //   if (isRequestOngoing.current) return false;
  //   isRequestOngoing.current = true;
  //   setIsBusy(true);
  //   let returnValue = false;
  //   try {
  //     const response = await AxiosAuth.post<ApiResponseContainer<{}>>(
  //       AppRouteApi.Auth.Logout()
  //     );
  //     if (response.data.isSuccess) {
  //       dispatch(ActionApp.ResetStore());
  //     }
  //     returnValue = true;
  //   } catch (e) {}
  //   dispatch(ActionApp.ResetStore());
  //   isRequestOngoing.current = false;
  //   setIsBusy(false);
  //   return returnValue;
  // }, [dispatch]);

  const logOut = React.useCallback(() => {
    dispatch(ActionApp.ResetStore());
    window.location.href = AppRouteUi.Login.Root();
  }, [dispatch]);

  const redirectToDefaultRoute = React.useCallback(() => {
    if (store.isLoggedIn && store.role)
      historyPush(AppRouteUi.GetRouteByRole(store.role));
  }, [store.isLoggedIn, store.role, historyPush]);

  return {
    isBusy,
    logIn,
    logOut,
    redirectToDefaultRoute,
  };
}
